import type { ApiReturnCampaign } from '@highimpact-frontend/api-types';
import assert from 'assert';
import { forwardRef, useEffect, useState, type LegacyRef } from 'react';
import { useParams } from 'react-router-dom';
import campaignService from '../../../api/services/config/campaign/CampaignService';
import PermissionDecoratorV2 from '../../../components/utils/permission/PermissionDecoratorV2';
import StateDecorator from '../../../components/utils/state/StateDecorator';
import { resetExternalStateWithParams } from '../../../store/data/slice';
import { isDataState } from '../../../store/data/state';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { removeRedundantByKey } from '../../../utils/functions';
import FilteredCampaigns from '../../management/campaigns/FilteredCampaigns';
import SiteDecorator from '../utils/SiteDecorator';

function SiteCampaignsComponent(
  _props: object,
  ref: LegacyRef<HTMLDivElement>
) {
  const dispatch = useAppDispatch();

  const [campaigns, setCampaigns] = useState<ApiReturnCampaign[]>([]);
  const [status, setStatus] = useState<'idle' | 'loading' | 'data' | 'empty'>(
    'idle'
  );

  const { project_id, site_id } = useParams<{
    project_id: string;
    site_id: string;
  }>();

  const sites = useAppSelector((state) => state.data_slice.externals.sites);
  const projects = useAppSelector(
    (state) => state.data_slice.externals.projects
  );

  const thematics = useAppSelector(
    (state) => state.data_slice.externals.thematics
  );

  const loadCampaigns = (siteId: number) => {
    setStatus('loading');
    campaignService
      .getByEntity({ context: 'site', id: siteId })
      .then((response) => {
        setCampaigns(response);
        if (response.length === 0) {
          setTimeout(() => setStatus('empty'), 3000);
        } else {
          setStatus('data');
        }
      })
      .catch(() => setStatus('empty'));
  };

  const refresh = () => {
    if (site_id) {
      loadCampaigns(Number.parseInt(site_id));
      dispatch(
        resetExternalStateWithParams({
          params: { site_id: site_id! },
          external_key: 'campaigns',
        })
      );
    }
  };

  useEffect(() => {
    if (site_id) {
      loadCampaigns(Number.parseInt(site_id));
    }
  }, [site_id]);

  return SiteDecorator(ref, () => {
    return StateDecorator([sites, thematics, projects], ref, () => {
      if (!site_id || !project_id)
        return (
          <div>
            Erreur : Les données de campagnes n'ont pas pu être récupérées
          </div>
        );

      assert(isDataState(sites));
      assert(isDataState(thematics));
      assert(isDataState(projects));

      const project = Object.values(projects.data).find(
        (pr) => pr.id === Number.parseInt(project_id)
      );
      if (!project) return <div>Le projet n'est pas valide</div>;

      const site = sites.data[Number.parseInt(site_id)];

      if (status === 'loading')
        return (
          <div className={`sites-grid`}>
            <div
              style={{
                backgroundColor: 'white',
                padding: '2em',
                height: '100%',
              }}
            >
              <div className="uk-flex uk-flex-center uk-flex-middle">
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                  }}
                >
                  <span uk-spinner="ratio: 3"></span>
                  <h3>Veuillez patienter !</h3>
                  <p>Chargement en cours...</p>
                </div>
              </div>
            </div>
          </div>
        );

      return (
        <div
          style={{
            backgroundColor: 'white',
            padding: '32px',
            height: 'fit-content',
          }}
        >
          <PermissionDecoratorV2
            item={'campaigns_page'}
            project_id={Number(project_id)}
            site_id={Number(site_id)}
          >
            <FilteredCampaigns
              campaigns={campaigns}
              projectId={Number.parseInt(project_id)}
              sites={site ? [site] : []}
              thematics={removeRedundantByKey(
                Object.values(thematics.data),
                'name'
              )
                .filter((c) => !c.name.toLowerCase().startsWith('météo'))
                .sort((a, b) => {
                  const aName = a.name.toLowerCase();
                  const bName = b.name.toLowerCase();
                  if (aName < bName) return -1;
                  if (aName > bName) return 1;
                  return 0;
                })}
              refresh={refresh}
            />
          </PermissionDecoratorV2>
        </div>
      );
    });
  });
}

export default forwardRef(SiteCampaignsComponent);
